import { ContactSelectors, ContactThunks } from 'modules/contactPage/redux';
import { resetState } from 'modules/contactPage/redux/slice';
import { ContactPathsEnum } from 'modules/contactPage/routes';
import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import { LoginSelectors } from 'modules/landingPage/components/login/redux';
import { ErrorFields } from 'modules/registerPage/components/common/RegistrationUser';
import { ContactFormData } from 'modules/types/ContactFormData';
import { InputWithLabel, SuccessAction } from 'modules/ui';
import { FormButtons, FormPane, FormPaneContainer } from 'modules/ui/form';
import { useForm } from 'modules/ui/form/useForm';
import { TextareaWithLabel } from 'modules/ui/textarea';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ValidationType } from 'types/ValidationType';
import { PHONE_NUMBER_PREFIX } from '../../../hooks/validationHooks/useInputValidator';
import { MaxFieldLength } from '../../../ui/input/common/MaxFieldLength';

export interface ContactFormProps {
  errorText?: ErrorFields;
  setFormTitle: (value: string) => void;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  errorText,
  setFormTitle,
}) => {
  const [formData, setFormData] = useState<ContactFormData>({});
  const [sendSuccess, setSendSuccess] = useState<boolean>();
  const [validate, setValidate] = useState<boolean>(false);
  const { validateForm } = useForm();

  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const history = useHistory();

  const sending = useAppSelector(ContactSelectors.selectSending);
  const sendingError = useAppSelector(ContactSelectors.selectSendingError);
  const user = useAppSelector(LoginSelectors.selectUser);

  const phoneValidators = user
    ? [ValidationType.REQUIRED, ValidationType.PHONE]
    : [ValidationType.PHONE];

  const handleContactFormSubmit = useCallback(
    (event: any) => {
      event.preventDefault();
      setValidate(true);

      validateForm(event)
        ? window.scrollTo(0, 0)
        : dispatch?.(ContactThunks.sendContactMessage(formData));
    },
    [dispatch, formData, validateForm],
  );

  useEffect(() => {
    if (sending === false && sendingError) {
      setSendSuccess(false);
      dispatch(resetState());
    } else if (sending === false && !sendingError) {
      setSendSuccess(true);
      dispatch(resetState());
    }
  }, [dispatch, sending, sendingError]);

  useEffect(() => {
    if (user) {
      setFormData({
        ...formData,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: {
          areaCode: user.phone?.areaCode,
          number: user.phone?.number,
        },
        email: user.email,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (sendSuccess === true) {
      setFormTitle('');
    }
  }, [sendSuccess, setFormTitle]);

  return (
    <>
      {sendSuccess === undefined && (
        <FormPaneContainer noValidate onSubmit={handleContactFormSubmit}>
          <FormPane width="45%">
            <InputWithLabel
              name="firstName"
              label={t('contactPage.contactForm.firstName')}
              placeholderText={t(
                'contactPage.contactForm.firstNamePlaceholder',
              )}
              required
              onBlur={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
              validators={[ValidationType.REQUIRED]}
              validate={validate}
              errorText={errorText?.firstName}
              maxLength={MaxFieldLength.Name}
              defaultValue={user?.firstName}
            />
            <InputWithLabel
              name="lastName"
              label={t('contactPage.contactForm.lastName')}
              placeholderText={t('contactPage.contactForm.lastNamePlaceholder')}
              required
              onBlur={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
              validators={[ValidationType.REQUIRED]}
              validate={validate}
              errorText={errorText?.lastName}
              maxLength={MaxFieldLength.Name}
              defaultValue={user?.lastName}
            />
            <InputWithLabel
              name="phone"
              label={t('contactPage.contactForm.phone')}
              placeholderText={t('contactPage.contactForm.phonePlaceholder')}
              required={!!user}
              onBlur={(e) =>
                setFormData({
                  ...formData,
                  phone: {
                    areaCode: PHONE_NUMBER_PREFIX,
                    number: e.target.value,
                  },
                })
              }
              validators={phoneValidators}
              validate={validate}
              errorText={errorText?.contactPersonPhone?.number}
              type="tel"
              maxLength={MaxFieldLength.Phone}
              prefix={user?.phone?.areaCode || PHONE_NUMBER_PREFIX}
              defaultValue={user?.phone?.number}
            />
            <InputWithLabel
              type="email"
              name="email"
              label={t('contactPage.contactForm.email')}
              placeholderText={t('contactPage.contactForm.emailPlaceholder')}
              required
              onBlur={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              validators={[ValidationType.REQUIRED, ValidationType.EMAIL]}
              validate={validate}
              errorText={errorText?.email}
              maxLength={MaxFieldLength.Email}
              defaultValue={user?.email}
            />
            <InputWithLabel
              name="contractNumber"
              label={t('contactPage.contactForm.contractNumber')}
              placeholderText={t(
                'contactPage.contactForm.contractNumberPlaceholder',
              )}
              onBlur={(e) =>
                setFormData({ ...formData, contractNumber: e.target.value })
              }
            />
          </FormPane>
          <FormPane>
            <TextareaWithLabel
              name="message"
              rows={11}
              label={t('contactPage.contactForm.message')}
              placeholderText={t('contactPage.contactForm.messagePlaceholder')}
              required
              onBlur={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
              validators={[ValidationType.REQUIRED, ValidationType.MESSAGE]}
              validate={validate}
              maxLength={MaxFieldLength.Message}
            />
            <FormButtons
              text={t('contactPage.contactForm.submit')}
              rightAligned
            />
          </FormPane>
        </FormPaneContainer>
      )}
      {sendSuccess === true && (
        <SuccessAction
          title={t('contactPage.contactForm.successMessage')}
          buttonText={t('contactPage.contactForm.successButtonText')}
          onClick={() => {
            history.push('/');
          }}
        />
      )}
      {sendSuccess === false && (
        <SuccessAction
          success={false}
          title={t('contactPage.contactForm.failMessage')}
          buttonText={t('contactPage.contactForm.failButtonText')}
          onClick={() => {
            setSendSuccess(undefined);
            history.push(ContactPathsEnum.contactPage);
          }}
        />
      )}
    </>
  );
};
